import api from "./api";

/**
 * Loyalty — the branch programmes, the rewards, the members holding a card, and the
 * desk's redeem flow. Every call is gated on the server by `loyalty.*` and by
 * the plan (`403 plan_feature_unavailable`); balances and points are always
 * the server's numbers.
 */

/* ------------------------------------------------------------ programmes */

/*
 * One programme per BRANCH: it decides how a visit at that branch earns. The
 * customer's balance stays one wallet per business. A branch without a
 * programme earns nothing.
 */

/**
 * GET /business/loyalty/programs — every reachable branch's programme, plus
 * `branches` (each with its `program_id`, null = none) for the branch pickers
 * and the "no programme here" warnings.
 */
export const fetchPrograms = async () => {
  const { data } = await api.get("/business/loyalty/programs");
  return {
    programs: data.data.programs,
    branches: data.meta?.branches ?? [],
    options: data.meta?.options ?? null,
    currency: data.meta?.currency ?? null,
  };
};

/** GET /business/loyalty/programs/{id} — one programme and its current version. */
export const fetchBranchProgram = async (id) => {
  const { data } = await api.get(`/business/loyalty/programs/${id}`);
  return { program: data.data.program, version: data.data.version, currency: data.meta?.currency ?? null };
};

/** POST /business/loyalty/programs — a new programme (version 1) for a branch without one. */
export const createProgram = async (values) => {
  const { data } = await api.post("/business/loyalty/programs", values);
  return { program: data.data.program, version: data.data.version };
};

/**
 * PUT /business/loyalty/programs/{id} (+ optional `change_note`, `branch_id`
 * to move it to a branch without one). A save that changes a setting opens a
 * new version; an unchanged one does not.
 */
export const updateProgram = async (id, values) => {
  const { data } = await api.put(`/business/loyalty/programs/${id}`, values);
  return {
    program: data.data.program,
    version: data.data.version,
    versionCreated: Boolean(data.data.version_created),
  };
};

/**
 * POST /business/loyalty/programs/{id}/copy — new programme(s), version 1
 * each, from this one's settings overridden by `values`. `branch_id` for one
 * target, `branch_ids` for several. The source is untouched.
 */
export const copyProgram = async (id, values) => {
  const { data } = await api.post(`/business/loyalty/programs/${id}/copy`, values);
  return { programs: data.data.programs, versions: data.data.versions, message: data.message };
};

/** POST /business/loyalty/programs/{id}/activate | deactivate — a new version unless already so. */
export const setProgramActive = async (id, active) => {
  const { data } = await api.post(`/business/loyalty/programs/${id}/${active ? "activate" : "deactivate"}`);
  return { program: data.data.program, version: data.data.version };
};

/** GET /business/loyalty/programs/{id}/versions — one programme's history, newest first. */
export const fetchProgramVersions = async (programId, { page = 1, perPage = 20 } = {}) => {
  const { data } = await api.get(`/business/loyalty/programs/${programId}/versions`, {
    params: { page, per_page: perPage },
  });
  return { versions: data.data.versions, meta: data.meta };
};

/** GET /business/loyalty/programs/{id}/versions/{version} — one version in full, with what changed. */
export const fetchProgramVersion = async (programId, id) => {
  const { data } = await api.get(`/business/loyalty/programs/${programId}/versions/${id}`);
  return { version: data.data.version, currency: data.meta?.currency ?? null };
};

/* --------------------------------------------------------------- rewards */

/** GET /business/loyalty/rewards */
export const fetchRewards = async ({ active } = {}) => {
  const params = {};
  if (active !== undefined && active !== "") params.active = active ? 1 : 0;
  const { data } = await api.get("/business/loyalty/rewards", { params });
  return data.data.rewards;
};

export const createReward = async (values) => {
  const { data } = await api.post("/business/loyalty/rewards", values);
  return data.data.reward;
};

export const updateReward = async (id, values) => {
  const { data } = await api.put(`/business/loyalty/rewards/${id}`, values);
  return data.data.reward;
};

/** DELETE retires a reward (soft): claims already issued keep working. */
export const retireReward = (id) => api.delete(`/business/loyalty/rewards/${id}`);

/* --------------------------------------------------------------- members */

/** GET /business/loyalty/members */
export const fetchMembers = async ({ page = 1, perPage = 20, search = "", sort = "recent" } = {}) => {
  const params = { page, per_page: perPage, sort };
  if (search) params.search = search;
  const { data } = await api.get("/business/loyalty/members", { params });
  return { members: data.data.members, meta: data.meta };
};

/** GET /business/clients/{client}/loyalty — the card (opens one if the client has none). */
export const fetchCard = async (clientId, branchId) => {
  const params = branchId ? { branch: branchId } : {};
  const { data } = await api.get(`/business/clients/${clientId}/loyalty`, { params });
  return data.data.card;
};

/**
 * POST /business/clients/{client}/loyalty — the explicit "create card". Reads
 * never open an account (`card.has_account` false until a write does).
 */
export const createCard = async (clientId) => {
  const { data } = await api.post(`/business/clients/${clientId}/loyalty`);
  return data.data.card;
};

/** GET /business/clients/{client}/loyalty/transactions */
export const fetchTransactions = async (clientId, { page = 1, perPage = 20 } = {}) => {
  const { data } = await api.get(`/business/clients/${clientId}/loyalty/transactions`, {
    params: { page, per_page: perPage },
  });
  return { transactions: data.data.transactions, meta: data.meta };
};

/** POST /business/clients/{client}/loyalty/adjust — signed points, a reason from the list, a note. */
export const adjustBalance = async (clientId, { points, reason, note }) => {
  const { data } = await api.post(`/business/clients/${clientId}/loyalty/adjust`, {
    points,
    reason,
    note: note || null,
  });
  return data.data;
};

/* ---------------------------------------------------------------- redeem */

/** GET /business/loyalty/lookup — a scanned / typed card code. 404 when nothing matches. */
export const lookupCard = async (token, branchId) => {
  const params = { token };
  if (branchId) params.branch = branchId;
  const { data } = await api.get("/business/loyalty/lookup", { params });
  return data.data.card;
};

/** POST /business/clients/{client}/loyalty/redemptions — claim a reward for the client (takes the points). */
export const claimReward = async (clientId, { rewardId, branchId }) => {
  const { data } = await api.post(`/business/clients/${clientId}/loyalty/redemptions`, {
    reward_id: rewardId,
    branch_id: branchId || null,
  });
  return data.data;
};

/** POST /business/loyalty/redemptions/redeem — use a claimed reward by its code. */
export const redeemCode = async ({ code, branchId, appointmentId, appointmentServiceId }) => {
  const { data } = await api.post("/business/loyalty/redemptions/redeem", {
    code,
    branch_id: branchId || null,
    appointment_id: appointmentId || null,
    // A free service used on the visit: which line is free (default: the first match).
    appointment_service_id: appointmentServiceId || null,
  });
  return data.data.redemption;
};

/** DELETE /business/loyalty/redemptions/{id} — give an unused reward back (points return). */
export const cancelRedemption = async (id) => {
  const { data } = await api.delete(`/business/loyalty/redemptions/${id}`);
  return data.data.redemption;
};

/* --------------------------------------------------------------- helpers */

/** A translatable name in the current language, else English. */
export const localText = (value, language) =>
  value && typeof value === "object" ? value[language] || value.en || "" : value || "";

/**
 * "Spend 100 → earn N points" for the settings preview, in whole numbers only:
 * the rate is read as hundredths (two decimals, like the API) and the result
 * floored — the same rule the server applies. Illustration, not a quote.
 */
export const examplePoints = (spend, perCurrency) => {
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(String(perCurrency ?? "").trim());
  if (!match) return 0;
  const hundredths = Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0"));
  return Math.floor((spend * hundredths) / 100);
};
